import { useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Clock, Loader2, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { COMPANY } from "@/data/site";
import Reveal from "@/components/Reveal";
import PageHeader, { usePageTitle } from "@/components/PageHeader";

const EMPTY = { nome: "", email: "", telefono: "", messaggio: "" };
const field =
  "mt-2 w-full rounded-md border border-white/15 bg-ink px-4 py-3 text-sm text-white placeholder:text-white/30 outline-none transition-colors focus:border-brand";

const INFO = [
  { icon: MapPin, label: "Sede", value: COMPANY.address },
  { icon: Phone, label: "Telefono", value: COMPANY.phoneDisplay, href: `tel:${COMPANY.phoneRaw}` },
  { icon: Mail, label: "Email", value: COMPANY.email, href: `mailto:${COMPANY.email}` },
  { icon: Mail, label: "PEC", value: COMPANY.pec, href: `mailto:${COMPANY.pec}` },
  { icon: Clock, label: "Orari", value: COMPANY.hours },
];

const ContactForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [privacy, setPrivacy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome.trim() || !form.email.trim() || !form.messaggio.trim()) {
      toast.error("Compila nome, email e messaggio.");
      return;
    }
    if (!privacy) {
      toast.error("Devi accettare il trattamento dei dati (GDPR).");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Messaggio inviato! Ti ricontatteremo al più presto.");
      setForm(EMPTY);
      setPrivacy(false);
      setSent(true);
    } catch {
      toast.error(`Invio non riuscito. Scrivici a ${COMPANY.email}`);
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="contact-form" className="rounded-xl border border-white/10 bg-surface p-7 md:p-10">
      <h2 className="font-display text-3xl font-bold uppercase text-white">Scrivici</h2>
      {sent && (
        <p data-testid="contact-success" className="mt-4 flex items-center gap-2 text-sm text-emerald-400">
          <CheckCircle2 className="h-4 w-4" /> Messaggio inviato correttamente.
        </p>
      )}
      <div className="mt-8 space-y-5">
        <label className="block text-[11px] font-semibold uppercase tracking-[0.2em] text-white/50">
          Nome e cognome *
          <input data-testid="contact-input-nome" value={form.nome} onChange={set("nome")} className={field} placeholder="Mario Rossi" />
        </label>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <label className="block text-[11px] font-semibold uppercase tracking-[0.2em] text-white/50">
            Email *
            <input type="email" data-testid="contact-input-email" value={form.email} onChange={set("email")} className={field} placeholder="nome@email.it" />
          </label>
          <label className="block text-[11px] font-semibold uppercase tracking-[0.2em] text-white/50">
            Telefono
            <input type="tel" data-testid="contact-input-telefono" value={form.telefono} onChange={set("telefono")} className={field} placeholder="+39 ..." />
          </label>
        </div>
        <label className="block text-[11px] font-semibold uppercase tracking-[0.2em] text-white/50">
          Messaggio *
          <textarea rows={5} data-testid="contact-input-messaggio" value={form.messaggio} onChange={set("messaggio")} className={field} placeholder="Raccontaci il tuo progetto..." />
        </label>
        <label className="flex items-start gap-3 text-xs leading-relaxed text-white/55">
          <input
            type="checkbox"
            checked={privacy}
            onChange={(e) => setPrivacy(e.target.checked)}
            data-testid="contact-input-privacy"
            className="mt-0.5 h-4 w-4 shrink-0 accent-[#2e9ee6]"
          />
          <span>
            Ho letto la{" "}
            <Link to="/privacy-policy" data-testid="contact-privacy-link" className="text-brand underline">Privacy Policy</Link>{" "}
            e la{" "}
            <Link to="/cookie-policy" data-testid="contact-cookie-link" className="text-brand underline">Cookie Policy</Link>{" "}
            e accetto il trattamento dei dati personali ai sensi del Regolamento UE 2016/679 (GDPR). *
          </span>
        </label>
        <button
          type="submit"
          disabled={sending}
          data-testid="contact-submit-button"
          className="inline-flex w-full items-center justify-center gap-3 rounded-md bg-brand px-8 py-4 text-xs font-semibold uppercase tracking-[0.18em] text-white transition-colors hover:bg-brand-hover disabled:opacity-60"
        >
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          {sending ? "Invio in corso..." : "Invia messaggio"}
        </button>
      </div>
    </form>
  );
};

export default function Contatti() {
  usePageTitle("/contatti");
  return (
    <main data-testid="contatti-page">
      <PageHeader
        testId="contatti-header"
        eyebrow="Contatti"
        title="Parliamone"
        lead="Sopralluogo, progettazione e preventivo. Raccontaci le tue esigenze e ti risponderemo con una soluzione su misura."
      />
      <section className="bg-ink">
        <div className="mx-auto grid max-w-[1600px] grid-cols-1 gap-14 px-6 py-24 md:px-12 md:py-32 lg:grid-cols-12">
          <Reveal className="space-y-8 lg:col-span-5">
            {INFO.map(({ icon: Icon, label, value, href }) => (
              <div key={label} className="flex gap-5 border-t border-white/10 pt-6">
                <Icon className="mt-1 h-5 w-5 shrink-0 text-brand" />
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/40">{label}</div>
                  {href ? (
                    <a href={href} data-testid={`contact-info-${label.toLowerCase()}`} className="mt-1 block text-lg text-white hover:text-brand">{value}</a>
                  ) : (
                    <div data-testid={`contact-info-${label.toLowerCase()}`} className="mt-1 text-lg text-white">{value}</div>
                  )}
                </div>
              </div>
            ))}
            <a
              href={COMPANY.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp-button"
              className="inline-flex items-center gap-3 rounded-md border border-white/15 px-8 py-4 text-xs font-semibold uppercase tracking-[0.18em] text-white transition-colors hover:border-[#25D366] hover:text-[#25D366]"
            >
              Scrivici su WhatsApp
            </a>
            <p className="text-xs text-white/35">{COMPANY.legal} — P.IVA {COMPANY.piva}</p>
          </Reveal>
          <Reveal delay={150} className="lg:col-span-7">
            <ContactForm />
          </Reveal>
        </div>
      </section>
    </main>
  );
}
